import { Helmet } from 'react-helmet'
import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import MainHeader from '../../components/MainHeader'
import MainFooter from '../../components/MainFooter'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faPaperPlane, faRobot, faFileLines, faUser, faPhone } from '@fortawesome/free-solid-svg-icons'

import './Chat.css'
import './MChat.css'
import { APPMODE, boxvar2_1, URL_STRAPI, MarkdownViewer, saveToniSummary, getToniSummary, saveToniContact, boxvar2_2, TONI_MAIL_MESSAGE_MAX_CHARS } from '../../core.js'

const CHAT_ENDPOINT = `${URL_STRAPI}/api/chatbot`
const SUMMARIZE_ENDPOINT = `${URL_STRAPI}/api/chatbot/summarize`
const SUGGESTIONS_ENDPOINT = `${URL_STRAPI}/api/chatbot/suggestions`

const HISTORY_LIMIT = 8

const WELCOME = {
    role: 'assistant',
    content: 'Hi! Ich bin **ToNi**, der digitale KI-Assistent von ToNetwork. Ich beantworte dir Fragen über uns und unsere Leistungen. Wie kann ich dir helfen?',
}

const FALLBACK_SUGGESTIONS = [
    'Was kostet eine Website?',
]

const COLLECT_QUESTIONS_STORAGE_KEY = 'toni_chat_collect_questions'
// const SHOW_COLLECT_TOGGLE = APPMODE !== 'prod'
const SHOW_COLLECT_TOGGLE = true

function readCollectQuestionsPreference() {
    try {
        const stored = localStorage.getItem(COLLECT_QUESTIONS_STORAGE_KEY)
        if (stored === '0') return false
        if (stored === '1') return true
    } catch (e) { /* localStorage nicht verfuegbar */ }
    if (APPMODE !== 'prod') return true
    return false
}

function chatHistory(messages, limit = HISTORY_LIMIT) {
    return messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-limit)
        .map((m) => ({ role: m.role, content: m.content }))
}

function stripMarkdown(text) {
    return String(text)
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\[(.*?)\]\([^)]*\)/g, '$1')
        .trim()
}

function formatChatTranscript(messages) {
    return messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => {
            const label = m.role === 'user' ? 'Du' : 'ToNi'
            return `${label}: ${stripMarkdown(m.content)}`
        })
        .join('\n\n')
}

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/** Dezente Haptik auf Touch-Geraeten (Vibration API). */
const CHAT_VIBRATE = {
    send: 12,
    reply: [10, 35, 10],
    success: [15, 40, 15],
    error: [25, 60, 25],
}

function chatVibrate(pattern) {
    if (typeof navigator?.vibrate !== 'function') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (!window.matchMedia('(pointer: coarse)').matches) return
    try { navigator.vibrate(pattern) } catch (e) { /* nicht unterstuetzt (z. B. iOS Safari) */ }
}

function splitName(fullName) {
    const name = fullName.trim().replace(/\s+/g, ' ')
    const sp = name.indexOf(' ')
    if (sp === -1) return { firstname: name, lastname: '' }
    return { firstname: name.slice(0, sp), lastname: name.slice(sp + 1) }
}

export default function Chat() {
    const [messages, setMessages] = useState([WELCOME])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [summarizing, setSummarizing] = useState(false)
    const [summary, setSummary] = useState('')
    const [error, setError] = useState('')

    const [contactName, setContactName] = useState('')
    const [contactEmail, setContactEmail] = useState('')
    const [contactPhone, setContactPhone] = useState('')
    const [contactConsent, setContactConsent] = useState(false)
    const [contactError, setContactError] = useState('')
    const [suggestions, setSuggestions] = useState(FALLBACK_SUGGESTIONS)
    const [collectQuestions, setCollectQuestions] = useState(readCollectQuestionsPreference)

    const listRef = useRef(null)
    const inputRef = useRef(null)

    const userMessageCount = messages.filter((m) => m.role === 'user').length
    const busy = loading || summarizing

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight
        }
    }, [messages, loading, summarizing, summary])

    useEffect(() => {
        let cancelled = false

        fetch(SUGGESTIONS_ENDPOINT)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (cancelled) return
                const list = data && Array.isArray(data.suggestions) ? data.suggestions : []
                if (list.length > 0) setSuggestions(list)
            })
            .catch(() => { })

        return () => { cancelled = true }
    }, [])

    function onCollectQuestionsToggle(e) {
        const enabled = e.target.checked
        setCollectQuestions(enabled)
        try {
            localStorage.setItem(COLLECT_QUESTIONS_STORAGE_KEY, enabled ? '1' : '0')
        } catch (err) { /* ignore */ }
    }

    async function sendMessage(text) {
        const content = (text ?? input).trim()
        if (!content || busy) return

        setError('')
        setInput('')

        const nextMessages = [...messages, { role: 'user', content }]
        setMessages(nextMessages)
        setLoading(true)
        chatVibrate(CHAT_VIBRATE.send)

        try {
            const res = await fetch(CHAT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: chatHistory(nextMessages),
                    collectQuestions,
                }),
            })

            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const data = await res.json()
            const reply = (data && (data.reply || data.message)) || ''

            if (!reply) throw new Error('Leere Antwort')

            setMessages((prev) => [...prev, { role: 'assistant', content: reply }])
            chatVibrate(CHAT_VIBRATE.reply)
        } catch (e) {
            setError('Es gab ein Problem mit dem Assistenten. Bitte versuche es später erneut oder nutze unsere Kontaktmöglichkeiten.')
            chatVibrate(CHAT_VIBRATE.error)
        } finally {
            setLoading(false)
            if (inputRef.current) inputRef.current.focus()
        }
    }

    async function createSummary() {
        if (userMessageCount < 1 || busy) return

        setError('')
        setSummarizing(true)

        try {
            const res = await fetch(SUMMARIZE_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: chatHistory(messages, 24) }),
            })

            if (!res.ok) throw new Error(`HTTP ${res.status}`)

            const data = await res.json()
            const text = (data && data.summary) || ''

            if (!text) throw new Error('Leere Zusammenfassung')

            setSummary(text)
            saveToniSummary(text)

            setMessages((prev) => [...prev, {
                role: 'assistant',
                content: `**Zusammenfassung deines Anliegens:**\n\n${text}\n\nHinterlasse unten kurz deine Kontaktdaten – dann übernehmen wir Zusammenfassung und Kontakt direkt ins Kontaktformular, und wir melden uns bei dir.`,
            }])
            chatVibrate(CHAT_VIBRATE.success)
        } catch (e) {
            setError('Die Zusammenfassung konnte nicht erstellt werden. Bitte versuche es erneut oder nutze unsere Kontaktseite direkt.')
            chatVibrate(CHAT_VIBRATE.error)
        } finally {
            setSummarizing(false)
        }
    }

    function validateContact() {
        if (contactName.trim().length < 2) return 'Bitte gib deinen Namen an.'
        if (!EMAIL_REGEX.test(contactEmail.trim())) return 'Bitte gib eine gültige E-Mail-Adresse an.'
        if (!contactConsent) return 'Bitte bestätige die Verarbeitung deiner Kontaktdaten.'
        return ''
    }

    function buildContact() {
        const name = contactName.trim().replace(/\s+/g, ' ')
        const { firstname, lastname } = splitName(name)
        return {
            name,
            firstname,
            lastname,
            email: contactEmail.trim(),
            phone: contactPhone.trim(),
        }
    }

    function persistForTransfer() {
        const contact = buildContact()
        saveToniContact(contact)
        if (summary) saveToniSummary(summary)
        return contact
    }

    function goToContactForm() {
        const err = validateContact()
        if (err) { setContactError(err); return }
        setContactError('')
        persistForTransfer()
        window.open('/support?from=toni#hubspotform', '_blank', 'noopener,noreferrer')
    }

    function openMailWithSummary() {
        const err = validateContact()
        if (err) { setContactError(err); return }
        setContactError('')

        const contact = persistForTransfer()
        let text = summary || getToniSummary() || ''
        if (text.length > TONI_MAIL_MESSAGE_MAX_CHARS) {
            text = `${text.slice(0, TONI_MAIL_MESSAGE_MAX_CHARS)}\n\n[... Zusammenfassung gekürzt wegen E-Mail-Größenlimit ...]`
        }

        const contactBlock = [
            `Name: ${contact.name}`,
            `E-Mail: ${contact.email}`,
            contact.phone ? `Telefon: ${contact.phone}` : null,
        ].filter(Boolean).join('\n')

        const subject = encodeURIComponent('Anfrage via ToNi – ToNetwork')
        const body = encodeURIComponent(
            `Hallo ToNetwork,\n\n${text}\n\nMeine Kontaktdaten:\n${contactBlock}\n\n---\nÜbermittelt via ToNi-Chat auf tonetwork.de`
        )
        window.location.href = `mailto:hey@tonetwork.de?subject=${subject}&body=${body}`
    }

    function openMailWithTranscript() {
        const transcript = formatChatTranscript(messages)
        if (!transcript) return

        const maxBodyLen = TONI_MAIL_MESSAGE_MAX_CHARS
        let body = transcript
        if (body.length > maxBodyLen) {
            body = `${body.slice(0, maxBodyLen)}\n\n[... Verlauf gekürzt wegen E-Mail-Größenlimit ...]`
        }

        const subject = encodeURIComponent('Mein ToNi-Chatverlauf – ToNetwork')
        const mailBody = encodeURIComponent(
            `${body}\n\n---\nExportiert via ToNi-Chat auf tonetwork.de\n(Hinweis: Antworten von ToNi sind KI-generiert und unverbindlich.)`
        )
        window.location.href = `mailto:?subject=${subject}&body=${mailBody}`
    }

    function onKeyDown(e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    return (
        <div className='mainCon' id='mainCon'>

            <MainHeader selectedsite={''} disableanimation={false} />

            <Helmet>
                <meta charSet='utf-8' />
                <title>ToNi – KI-Assistent | ToNetwork</title>
                <meta name='robots' content='noindex' />
                <meta property='og:title' content='ToNi – KI-Assistent' />
                <meta property='og:description' content='Stelle ToNi deine Fragen zu Leistungen, Paketen und Preisen von ToNetwork.' />
                <meta name='description' content='Stelle ToNi deine Fragen zu Leistungen, Paketen und Preisen von ToNetwork.' />
            </Helmet>

            <section className='sec_next' id='sec_next--chat'>
                <div className='sec_next_cont' id='sec_next--chat--cont'>
                    <motion.div variants={boxvar2_1} className='chat_intro' initial='hidden' exit='hidden' whileInView='visible' viewport={{ once: false, margin: '-50px' }}>
                        <h2 className='chat_intro_heading'>
                            <FontAwesomeIcon icon={faRobot} className='chat_intro_icon' />
                            <span className='chat_intro_title'>Chatte mit ToNi</span>
                            <span className='chat_intro_badge'>NEU</span>
                        </h2>
                        <p className='chat_intro_text'>
                            KI-Assistent für Fragen zu Leistungen, Paketen und Preisen. Für ein konkretes Angebot verweist ToNi dich an uns persönlich.
                        </p>
                    </motion.div>

                    <motion.div variants={boxvar2_2} className='chat_window' initial='hidden' exit='hidden' whileInView='visible' viewport={{ once: false, margin: '-50px' }}>

                        {/* <div className='chat_window'> */}
                        <div className='chat_messages' ref={listRef}>
                            {messages.map((m, i) => (
                                <div key={i} className={`chat_msg chat_msg-${m.role}`}>
                                    <div className='chat_bubble'>
                                        {m.role === 'assistant'
                                            ? <MarkdownViewer content={m.content} />
                                            : m.content}
                                    </div>
                                </div>
                            ))}

                            {busy && (
                                <div className='chat_msg chat_msg-assistant'>
                                    <div className='chat_bubble chat_bubble-typing'>
                                        <span></span><span></span><span></span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {messages.length <= 1 && !busy && suggestions.length > 0 && (
                            <div className='chat_suggestions'>
                                {suggestions.map((s) => (
                                    <button key={s} type='button' className='chat_suggestion' onClick={() => sendMessage(s)}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* 
                        {userMessageCount >= 1 && !summary && !busy && (
                            <div className='chat_suggestions'>
                                <button type='button' className='chat_suggestion' onClick={createSummary}>
                                    Zusammenfassung für Kontakt
                                </button>
                            </div>
                        )} */}

                        {/* {userMessageCount >= 1 && !summary && !busy && (
                            <div className='chat_actions'>
                                <button type='button' className='chat_action chat_action-primary' onClick={createSummary}>
                                    <FontAwesomeIcon icon={faFileLines} /> Anliegen zusammenfassen
                                </button>
                            </div>
                        )} */}

                        {userMessageCount >= 1 && !busy && (
                            <div className='chat_actions'>
                                {!summary && (
                                    <button type='button' className='chat_action chat_action-primary' onClick={createSummary}>
                                        <FontAwesomeIcon icon={faFileLines} /> Anliegen zusammenfassen für Kontaktaufnahme
                                    </button>
                                )}
                                <button type='button' className='chat_action' onClick={openMailWithTranscript}>
                                    <FontAwesomeIcon icon={faEnvelope} /> Chatverlauf per E-Mail
                                </button>
                            </div>
                        )}

                        {summary && (
                            <div className='chat_actions chat_actions-transfer chat_contactform'>
                                <span className='chat_actions_label'>Deine Kontaktdaten für die Anfrage:</span>

                                <div className='chat_contact_field'>
                                    <FontAwesomeIcon icon={faUser} className='chat_contact_icon' />
                                    <input
                                        type='text'
                                        className='chat_contact_input'
                                        placeholder='Name *'
                                        autoComplete='name'
                                        value={contactName}
                                        maxLength={80}
                                        onChange={(e) => setContactName(e.target.value)}
                                    />
                                </div>

                                <div className='chat_contact_field'>
                                    <FontAwesomeIcon icon={faEnvelope} className='chat_contact_icon' />
                                    <input
                                        type='email'
                                        className='chat_contact_input'
                                        placeholder='E-Mail *'
                                        autoComplete='email'
                                        value={contactEmail}
                                        maxLength={120}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                    />
                                </div>

                                <div className='chat_contact_field'>
                                    <FontAwesomeIcon icon={faPhone} className='chat_contact_icon' />
                                    <input
                                        type='tel'
                                        className='chat_contact_input'
                                        placeholder='Telefon (optional)'
                                        autoComplete='tel'
                                        value={contactPhone}
                                        maxLength={40}
                                        onChange={(e) => setContactPhone(e.target.value)}
                                    />
                                </div>

                                <label className='chat_contact_consent'>
                                    <input
                                        type='checkbox'
                                        checked={contactConsent}
                                        onChange={(e) => setContactConsent(e.target.checked)}
                                    />
                                    <span>
                                        Ich bin einverstanden, dass meine Kontaktdaten zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden (siehe <a href='/rechtliches/datenschutz' target='_blank' rel='noopener noreferrer'>Datenschutz</a>).
                                    </span>
                                </label>

                                <span className='chat_contact_note'>
                                    Diese Kontaktdaten werden nicht an die KI übermittelt, sondern nur für deine Kontaktanfrage genutzt.
                                </span>

                                {contactError && <div className='chat_error chat_contact_error'>{contactError}</div>}

                                <div className='chat_contact_buttons'>
                                    <button type='button' className='chat_action chat_action-primary' onClick={goToContactForm}>
                                        Zum Kontaktformular
                                    </button>
                                    <button type='button' className='chat_action' onClick={openMailWithSummary}>
                                        <FontAwesomeIcon icon={faEnvelope} /> Per E-Mail senden
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && <div className='chat_error'>{error}</div>}

                        <div className='chat_inputbar'>
                            <textarea
                                ref={inputRef}
                                className='chat_input'
                                rows={1}
                                placeholder='Schreibe deine Frage…'
                                value={input}
                                maxLength={1000}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKeyDown}
                                disabled={busy}
                            />
                            <button
                                type='button'
                                className='chat_send'
                                onClick={() => sendMessage()}
                                disabled={busy || input.trim() === ''}
                                aria-label='Nachricht senden'
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </div>

                        {SHOW_COLLECT_TOGGLE && (
                            <label className='chat_collect_toggle'>
                                <input
                                    type='checkbox'
                                    className='chat_collect_toggle_input'
                                    checked={collectQuestions}
                                    onChange={onCollectQuestionsToggle}
                                />
                                <span className='chat_collect_toggle_track' aria-hidden='true' />
                                <span className='chat_collect_toggle_label'>
                                    Ich stimme zu, dass ToNetwork Chatnachrichten zur Qualitätssicherung temporär sammelt.&nbsp;
                                    <span className='chat_collect_toggle_hint'><a href='/rechtliches/datenschutz'>siehe Datenschutz</a></span>
                                </span>
                            </label>
                        )}

                        <p className='chat_disclaimer'>
                            ToNi ist ein KI-Assistent und kann Fehler machen. Die Angaben des Assistenten sind unverbindlich. Bitte gib keine sensiblen oder personenbezogenen Daten ein.
                            Deine Eingaben werden zur Beantwortung an Mistral AI (EU) übermittelt – siehe <a href='/rechtliches/datenschutz'>Datenschutz</a>. Mit Eingabe akzeptierst du diese Datenschutzbestimmungen.
                        </p>
                        {/* </div> */}
                    </motion.div>


                </div>
            </section>

            <div className='chat_page_spacer' aria-hidden='true' />

            <MainFooter />
        </div>
    )
}
